import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActionButtonComponent } from '../../../shared/components/action-button/action-button.component';

export interface TemplateData {
  id: string;
  name: string;
  pages: number;
  preview: string;
  description: string;
}

/**
 * Renders a preview card for a template with actions to select or preview that template.
 * @component
 * @selector app-template-preview-card
 * @example
 *   <app-template-preview-card
 *     [template]="sampleTemplate"
 *     [isLoading]="false"
 *     (templateSelect)="onTemplateSelected($event)"
 *     (templatePreview)="onTemplatePreview($event)">
 *   </app-template-preview-card>
 * @input TemplateData template - The template data object to display in the card (required).
 * @input boolean isLoading - When true, the card shows a loading state and action buttons are disabled (optional, default: false).
 * @output EventEmitter<TemplateData> templateSelect - Emitted when the user selects the template (select action).
 * @output EventEmitter<TemplateData> templatePreview - Emitted when the user requests a preview of the template (preview action).
 * @method onSelect(): void - Emits the templateSelect event with the current template.
 * @method onPreview(): void - Emits the templatePreview event with the current template; typically triggered by the preview button.
 * @property string eyeIconSrc - Path to the preview icon asset ('assets/images/eye.svg'), used by the preview button.
 * @lifecycle No Angular lifecycle hooks are implemented in this component; it reacts to input property values and emits events for external handling.
 */
@Component({
  selector: 'app-template-preview-card',
  standalone: true,
  imports: [CommonModule, ActionButtonComponent],
  templateUrl: './template-preview-card.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./template-preview-card.component.scss'],
})
export class TemplatePreviewCardComponent {
  @Input() template!: TemplateData;
  @Input() isLoading = false;
  @Output() templateSelect = new EventEmitter<TemplateData>();
  @Output() templatePreview = new EventEmitter<TemplateData>();

  // Eye icon path for preview button
  eyeIconSrc = 'assets/images/eye.svg';

  onSelect(): void {
    this.templateSelect.emit(this.template);
  }

  onPreview(): void {
    this.templatePreview.emit(this.template);
  }
}
